import { useEffect, useRef, useState } from 'react';
import { Loader2, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export default function MapContainer({ selectedEvent, onEventSelect, filteredEvents, searchQuery, userLocation, isActualUserLocation, onMarkerPosition, onRecenter }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const userLocationMarkerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(13);

  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // If no API key, use fallback mode
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_API_KEY_HERE') {
      setMapError(true);
      setIsLoading(false);
      return;
    }

    // Check if Google Maps script is already loaded
    if (window.google && window.google.maps && window.google.maps.Map) {
      // Give it a small delay to ensure everything is ready
      setTimeout(initializeMap, 50);
      return;
    }

    // Check if script is already being loaded (prevent multiple loads)
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script is loading, wait for it
      const checkAndInit = () => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          setTimeout(initializeMap, 100);
        } else {
          setTimeout(checkAndInit, 100);
        }
      };
      checkAndInit();
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Give Safari time to fully initialize the API
      setTimeout(initializeMap, 100);
    };
    script.onerror = () => {
      setMapError(true);
      setIsLoading(false);
      toast.error('Failed to load Google Maps. Using preview interface.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup markers safely
      try {
        markersRef.current.forEach(marker => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
      } catch (error) {
        console.debug('Error cleaning up markers:', error);
      }
      
      // Cleanup user location marker
      try {
        if (userLocationMarkerRef.current) {
          if (userLocationMarkerRef.current.pulseInterval) {
            clearInterval(userLocationMarkerRef.current.pulseInterval);
          }
          if (userLocationMarkerRef.current.outerCircle) {
            userLocationMarkerRef.current.outerCircle.setMap(null);
          }
          if (userLocationMarkerRef.current.innerCircle) {
            userLocationMarkerRef.current.innerCircle.setMap(null);
          }
          if (userLocationMarkerRef.current.dot) {
            userLocationMarkerRef.current.dot.setMap(null);
          }
        }
      } catch (error) {
        console.debug('Error cleaning up user location marker:', error);
      }
      
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Update markers when filteredEvents changes
  useEffect(() => {
    if (mapInstanceRef.current && window.google && window.google.maps) {
      try {
        // Clear existing markers
        markersRef.current.forEach(marker => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        markersRef.current = [];

        // Add new markers (with clustering logic based on zoom)
        const markers = addMarkersToMap(mapInstanceRef.current);
        finalizeMarkers(markers);
      } catch (error) {
        console.debug('Error updating markers:', error);
      }
    }
  }, [filteredEvents, currentZoom]);

  // Handle user location marker when map becomes ready or when GPS location changes
  useEffect(() => {
    if (!isLoading && mapInstanceRef.current && userLocation && isActualUserLocation) {
      updateUserLocationMarker(userLocation, mapInstanceRef.current);
    }
  }, [isLoading, userLocation, isActualUserLocation]);

  // Re-center map when user location becomes available and add marker
  useEffect(() => {
    if (mapInstanceRef.current && userLocation) {
      try {
        if (mapInstanceRef.current.panTo && mapInstanceRef.current.setZoom) {
          mapInstanceRef.current.panTo(userLocation);
          mapInstanceRef.current.setZoom(14);
        }
        // Only add user location marker if it's actual GPS location
        if (isActualUserLocation) {
          updateUserLocationMarker(userLocation, mapInstanceRef.current);
        } else {
          // Remove marker if location is not actual GPS
          if (userLocationMarkerRef.current) {
            if (userLocationMarkerRef.current.pulseInterval) {
              clearInterval(userLocationMarkerRef.current.pulseInterval);
            }
            if (userLocationMarkerRef.current.outerCircle) {
              userLocationMarkerRef.current.outerCircle.setMap(null);
            }
            if (userLocationMarkerRef.current.innerCircle) {
              userLocationMarkerRef.current.innerCircle.setMap(null);
            }
            if (userLocationMarkerRef.current.dot) {
              userLocationMarkerRef.current.dot.setMap(null);
            }
            userLocationMarkerRef.current = null;
          }
        }
      } catch (error) {
        console.debug('Error recentering map:', error);
      }
    }
  }, [userLocation, isActualUserLocation]);

  // Function to apply offsets to markers at the same location
  const applyMarkerOffsets = (events) => {
    // Group events by location (round to 4 decimal places to catch nearby events - approx 11 meters)
    const locationGroups = {};
    
    events.forEach((event, index) => {
      const lat = parseFloat(event.position.lat).toFixed(4);
      const lng = parseFloat(event.position.lng).toFixed(4);
      const key = `${lat},${lng}`;
      
      if (!locationGroups[key]) {
        locationGroups[key] = [];
      }
      locationGroups[key].push({ event, originalIndex: index });
    });
    
    // Apply circular offsets to overlapping events
    const processedEvents = [...events];
    
    Object.values(locationGroups).forEach(group => {
      if (group.length > 1) {
        // Multiple events at same location - apply circular offset
        console.log(`Found ${group.length} events at same location - applying offsets`);
        const radius = 0.0003; // Approx 33 meters offset
        const angleStep = (2 * Math.PI) / group.length;
        
        group.forEach((item, i) => {
          const angle = i * angleStep;
          const offsetLat = radius * Math.cos(angle);
          const offsetLng = radius * Math.sin(angle);
          
          processedEvents[item.originalIndex] = {
            ...item.event,
            position: {
              lat: parseFloat(item.event.position.lat) + offsetLat,
              lng: parseFloat(item.event.position.lng) + offsetLng
            },
            originalPosition: item.event.position // Keep original for reference
          };
        });
      }
    });
    
    return processedEvents;
  };

  // Hybrid system: Clustering + Ring displacement
  const processEventsForMap = (events, map) => {
    if (!map) return events.map(e => ({ position: e.position, events: [e] }));
    
    try {
      const zoom = map.getZoom();
      if (!zoom || zoom === undefined) {
        return events.map(e => ({ position: e.position, events: [e] }));
      }
      
      // Always use smart clustering: cluster visually overlapping events, use ring displacement for same location
      return clusterOrDisplaceEvents(events, map);
      
    } catch (error) {
      console.warn('Event processing error:', error);
      return events.map(e => ({ position: e.position, events: [e] }));
    }
  };
  
  // Smart clustering and displacement logic
  const clusterOrDisplaceEvents = (events, map) => {
    const projection = map.getProjection();
    if (!projection) {
      return events.map(e => ({ position: e.position, events: [e] }));
    }
    
    const zoom = map.getZoom();
    
    // First, separate events into same-location groups and different-location events
    const locationGroups = {};
    const locationKey = (pos) => `${pos.lat.toFixed(6)},${pos.lng.toFixed(6)}`;
    
    events.forEach(event => {
      const key = locationKey(event.position);
      if (!locationGroups[key]) {
        locationGroups[key] = [];
      }
      locationGroups[key].push(event);
    });
    
    // Events at identical locations get ring displacement
    const sameLocationGroups = Object.values(locationGroups).filter(group => group.length > 1);
    const singleLocationEvents = Object.values(locationGroups)
      .filter(group => group.length === 1)
      .map(group => group[0]);
    
    // Apply ring displacement to same-location events
    const displacedEvents = [];
    sameLocationGroups.forEach(group => {
      const basePos = group[0].position;
      const radius = 0.0003; // Displacement radius
      group.forEach((event, index) => {
        const angle = (2 * Math.PI * index) / group.length;
        const displacedLat = basePos.lat + radius * Math.cos(angle);
        const displacedLng = basePos.lng + radius * Math.sin(angle);
        displacedEvents.push({
          ...event,
          position: { lat: displacedLat, lng: displacedLng },
          originalPosition: basePos
        });
      });
    });
    
    // Combine displaced events with single-location events
    const allEventsToProcess = [...displacedEvents, ...singleLocationEvents];
    
    // Now apply visual overlap clustering to all events
    return clusterVisuallyOverlappingEvents(allEventsToProcess, map);
  };
  
  // Original clustering logic for visually overlapping events
  const clusterVisuallyOverlappingEvents = (events, map) => {
    const projection = map.getProjection();
    if (!projection) {
      return events.map(e => ({ position: e.position, events: [e] }));
    }
    
    const zoom = map.getZoom();
    
    // Calculate pixel distance threshold based on marker size
    const isMobile = window.innerWidth <= 768;
    const MARKER_SIZE = isMobile ? 45 : 48;  // Match actual marker sizes
    const PADDING = 20;
    const OVERLAP_THRESHOLD = MARKER_SIZE + PADDING;
    
    // Convert events to pixel positions
    const eventsWithPixels = events.map(event => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.LatLng) {
          throw new Error('Google Maps API not available');
        }
        
        const latLng = new window.google.maps.LatLng(event.position.lat, event.position.lng);
        const point = projection.fromLatLngToPoint(latLng);
        
        if (!point || point.x === undefined || point.y === undefined) {
          throw new Error('Invalid projection point');
        }
        
        // Scale point based on zoom
        const scale = Math.pow(2, zoom);
        return {
          event,
          pixelX: point.x * scale,
          pixelY: point.y * scale,
          clustered: false
        };
      } catch (error) {
        console.debug('Error converting event to pixels:', error);
        return {
          event,
          pixelX: 0,
          pixelY: 0,
          clustered: false
        };
      }
    });
    
    const clusters = [];
    
    // Clustering algorithm
    eventsWithPixels.forEach((item, index) => {
      if (item.clustered) return;
      
      const cluster = {
        position: { ...item.event.position },
        events: [item.event],
        pixelX: item.pixelX,
        pixelY: item.pixelY,
        totalX: item.pixelX,
        totalY: item.pixelY,
        count: 1
      };
      
      item.clustered = true;
      
      // Find nearby events for this cluster
      eventsWithPixels.forEach((otherItem, otherIndex) => {
        if (otherIndex === index || otherItem.clustered) return;
        
        const dx = cluster.pixelX - otherItem.pixelX;
        const dy = cluster.pixelY - otherItem.pixelY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < OVERLAP_THRESHOLD) {
          cluster.events.push(otherItem.event);
          cluster.totalX += otherItem.pixelX;
          cluster.totalY += otherItem.pixelY;
          cluster.count++;
          
          // Update cluster center
          cluster.pixelX = cluster.totalX / cluster.count;
          cluster.pixelY = cluster.totalY / cluster.count;
          
          // Convert back to lat/lng
          try {
            if (window.google && window.google.maps && window.google.maps.Point) {
              const centerPoint = new window.google.maps.Point(
                cluster.pixelX / Math.pow(2, zoom),
                cluster.pixelY / Math.pow(2, zoom)
              );
              const centerLatLng = projection.fromPointToLatLng(centerPoint);
              if (centerLatLng && centerLatLng.lat && centerLatLng.lng) {
                cluster.position = {
                  lat: centerLatLng.lat(),
                  lng: centerLatLng.lng()
                };
              }
            }
          } catch (conversionError) {
            console.debug('Error converting cluster center:', conversionError);
          }
          
          otherItem.clustered = true;
        }
      });
      
      clusters.push(cluster);
    });
    
    return clusters;
  };
  
  // Ring displacement for same-location events (used in zoomed-in view)
  const displaceSameLocationEvents = (events, map) => {
    if (!map) return events.map(e => ({ position: e.position, events: [e] }));
    
    try {
      const zoom = map.getZoom();
      if (!zoom || zoom === undefined) {
        return events.map(e => ({ position: e.position, events: [e] }));
      }
      
      // Group events by location (with tolerance for floating point precision)
      const locationGroups = {};
      const LOCATION_TOLERANCE = 0.0001; // ~10 meters tolerance
      
      console.log('🔍 Processing', events.length, 'events for displacement at zoom', zoom);
      
      events.forEach(event => {
        // Round coordinates to avoid floating point issues
        const roundedLat = Math.round(event.position.lat / LOCATION_TOLERANCE) * LOCATION_TOLERANCE;
        const roundedLng = Math.round(event.position.lng / LOCATION_TOLERANCE) * LOCATION_TOLERANCE;
        const locationKey = `${roundedLat},${roundedLng}`;
        
        // Special logging for the test coordinates
        const testLat = 12.953126245121204;
        const testLng = 77.72671501801773;
        if (Math.abs(event.position.lat - testLat) < 0.001 && Math.abs(event.position.lng - testLng) < 0.001) {
          console.log('🎯 Found event near test coordinates:', event.title, event.position);
        }
        
        if (!locationGroups[locationKey]) {
          locationGroups[locationKey] = {
            originalPosition: event.position,
            events: []
          };
        }
        locationGroups[locationKey].events.push(event);
      });
      
      // Log groups with multiple events
      Object.entries(locationGroups).forEach(([key, group]) => {
        if (group.events.length > 1) {
          console.log('🔄 Displacing', group.events.length, 'events at location', key, 'titles:', group.events.map(e => e.title));
        }
      });
      
      // Create displaced positions for events at the same location
      const displacedEvents = [];
      
      Object.values(locationGroups).forEach(group => {
        if (group.events.length === 1) {
          // Single event, use original position
          displacedEvents.push({
            position: group.originalPosition,
            events: [group.events[0]]
          });
        } else {
          // Multiple events, create ring displacement
          const displacementRadius = getDisplacementRadius(zoom);
          console.log('💫 Creating ring displacement with radius:', displacementRadius, 'meters for', group.events.length, 'events');
          
          group.events.forEach((event, index) => {
            let displacedPosition;
            
            if (index === 0) {
              // First event stays at original location
              displacedPosition = group.originalPosition;
            } else {
              // Calculate displacement in a ring pattern
              const angle = (2 * Math.PI * (index - 1)) / (group.events.length - 1);
              const latOffset = (displacementRadius * Math.cos(angle)) / 111320; // ~1 degree lat = 111,320 meters
              const lngOffset = (displacementRadius * Math.sin(angle)) / (111320 * Math.cos(group.originalPosition.lat * Math.PI / 180));
              
              displacedPosition = {
                lat: group.originalPosition.lat + latOffset,
                lng: group.originalPosition.lng + lngOffset
              };
              
              console.log('📍 Displaced event', event.title, 'from', group.originalPosition, 'to', displacedPosition, 'angle:', angle, 'offset:', latOffset, lngOffset);
            }
            
            displacedEvents.push({
              position: displacedPosition,
              events: [event]
            });
          });
        }
      });
      
      console.log('✅ Displacement complete, returning', displacedEvents.length, 'positioned events');
      return displacedEvents;
      
    } catch (error) {
      console.warn('Displacement error:', error);
      // Fallback: return events as individual markers
      return events.map(e => ({ position: e.position, events: [e] }));
    }
  };

  // Calculate displacement radius based on zoom level
  const getDisplacementRadius = (zoom) => {
    // Displacement radius in meters - smaller at higher zoom levels
    if (zoom >= 17) return 15;  // Very close - 15m displacement
    if (zoom >= 15) return 25;  // Close - 25m displacement  
    if (zoom >= 13) return 50;  // Medium - 50m displacement
    if (zoom >= 11) return 100; // Far - 100m displacement
    return 200; // Very far - 200m displacement
  };

  const addMarkersToMap = (map) => {
    // Safety check
    if (!map || !window.google || !window.google.maps) {
      console.warn('Map or Google Maps API not available');
      return [];
    }
    
    // Apply hybrid processing: clustering when zoomed out, displacement when zoomed in
    const processedEvents = processEventsForMap(filteredEvents, map);
    
    const markers = processedEvents.map(processed => {
      if (processed.events.length === 1) {
        // Single event, show as normal marker
        return createEventMarker(processed.events[0], map, processed.position);
      } else {
        // Multiple events, show cluster marker
        return createClusterMarker(processed, map);
      }
    }).filter(marker => marker !== null); // Filter out any null markers
    
    return markers;
  };

  const createEventMarker = (event, map, customPosition = null) => {
        // Safety check for Google Maps API
        if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
          console.error('Google Maps API not available for marker creation');
          return null;
        }
        
        // Use custom position if provided (for displaced markers), otherwise use event position
        const markerPosition = customPosition || event.position;
        
        // Create custom HTML marker using Overlay
        class CustomMarker extends window.google.maps.OverlayView {
          constructor(position, icon, color, title, onClick) {
            super();
            this.position = position;
            this.icon = icon;
            this.color = color;
            this.title = title;
            this.onClick = onClick;
          }

          onAdd() {
            const div = document.createElement('div');
            div.className = 'custom-marker';
            div.style.position = 'absolute';
            div.style.cursor = 'pointer';
            
            // Responsive marker size - 20% smaller on mobile
            const markerSize = window.innerWidth <= 768 ? 45 : 48;  // Mobile: 45px (20% smaller), Desktop: 48px
            const fontSize = window.innerWidth <= 768 ? 22 : 24;     // Mobile: 22px (20% smaller), Desktop: 24px
            const borderWidth = window.innerWidth <= 768 ? 3 : 3;    // Consistent border
            
            div.innerHTML = `
              <div class="marker-wrapper" style="
                position: relative;
                width: ${markerSize}px;
                height: ${markerSize}px;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                filter: drop-shadow(0 0 4px ${this.color}30);
              ">
                <div class="marker-circle" style="
                  width: ${markerSize}px;
                  height: ${markerSize}px;
                  border-radius: 50%;
                  background: ${this.color}CC;
                  box-shadow: 0 4px 12px ${this.color}40, 0 0 8px ${this.color}20, inset 0 1px 2px rgba(255,255,255,0.5);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: ${fontSize}px;
                  border: ${borderWidth}px solid white;
                  transform: translateZ(0);
                  animation: marker-pulse 2s ease-in-out infinite;
                ">${this.icon}</div>
              </div>
            `;

            // Add hover effect - emphasize marker
            div.addEventListener('mouseenter', () => {
              div.querySelector('.marker-wrapper').style.transform = 'scale(1.15)';
            });
            div.addEventListener('mouseleave', () => {
              div.querySelector('.marker-wrapper').style.transform = 'scale(1)';
            });

            // Add click handler
            div.addEventListener('click', (e) => {
              const rect = div.getBoundingClientRect();
              const position = {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2
              };
              this.onClick(position);
              const wrapper = div.querySelector('.marker-wrapper');
              wrapper.style.animation = 'bounce 0.5s ease';
              setTimeout(() => {
                wrapper.style.animation = '';
              }, 500);
            });

            this.div = div;
            try {
              const panes = this.getPanes();
              if (panes && panes.overlayMouseTarget) {
                panes.overlayMouseTarget.appendChild(div);
              }
            } catch (error) {
              console.debug('Error adding marker to panes:', error);
            }
          }

          draw() {
            try {
              // Safety checks for all Google Maps API components
              if (!this.div) return;
              if (!window.google || !window.google.maps || !window.google.maps.LatLng) return;
              
              const overlayProjection = this.getProjection();
              if (!overlayProjection || typeof overlayProjection.fromLatLngToDivPixel !== 'function') return;
              
              // Validate position data
              if (!this.position || typeof this.position.lat !== 'number' || typeof this.position.lng !== 'number') return;
              
              const latLng = new window.google.maps.LatLng(this.position.lat, this.position.lng);
              if (!latLng) return;
              
              const position = overlayProjection.fromLatLngToDivPixel(latLng);
              
              if (position && typeof position.x === 'number' && typeof position.y === 'number') {
                // Center the marker on the position - 20% smaller on mobile
                const markerSize = window.innerWidth <= 768 ? 45 : 48;  // Mobile: 45px (20% smaller), Desktop: 48px
                
                this.div.style.left = (position.x - markerSize / 2) + 'px';
                this.div.style.top = (position.y - markerSize / 2) + 'px';
              }
            } catch (error) {
              // Silently handle draw errors (can happen during map transitions)
              console.debug('Marker draw error:', error);
            }
          }

          onRemove() {
            try {
              if (this.div && this.div.parentNode) {
                this.div.parentNode.removeChild(this.div);
              }
              this.div = null;
            } catch (error) {
              // Silently handle removal errors
              console.debug('Marker removal error:', error);
            }
          }
        }

        const marker = new CustomMarker(
          markerPosition,
          event.icon,
          getCategoryColor(event.l1_category || event.category),
          event.title,
          (position) => {
            // First center the map
            map.panTo(event.position);
            
            // Then after a short delay, open the event panel
            setTimeout(() => {
              onEventSelect(event);
              if (onMarkerPosition && position) {
                onMarkerPosition(position);
              }
            }, 100);
          }
        );

        marker.setMap(map);
        return marker;
  };

  const createClusterMarker = (cluster, map) => {
    // Safety check for Google Maps API
    if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
      console.error('Google Maps API not available for cluster creation');
      return null;
    }
    
    class ClusterMarker extends window.google.maps.OverlayView {
      constructor(position, count, onClick) {
        super();
        this.position = position;
        this.count = count;
        this.onClick = onClick;
      }

      onAdd() {
        const div = document.createElement('div');
        div.className = 'cluster-marker';
        div.style.position = 'absolute';
        div.style.cursor = 'pointer';
        
        // Calculate size based on count - 20% smaller on mobile
        const isMobile = window.innerWidth <= 768;
        const baseSize = isMobile ? 40 : 50;      // Mobile: 40px (20% smaller), Desktop: 50px
        const maxSize = isMobile ? 64 : 80;       // Mobile: 64px (20% smaller), Desktop: 80px
        const size = Math.min(baseSize + (this.count * 2), maxSize);
        
        // Adjust font sizes to fit within circle
        const numberFontSize = size > 50 ? '16px' : '14px';
        const labelFontSize = size > 50 ? '9px' : '8px';
        
        div.innerHTML = `
          <div class="cluster-wrapper" style="
            position: relative;
            width: ${size}px;
            height: ${size}px;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          ">
            <div class="cluster-circle" style="
              width: ${size}px;
              height: ${size}px;
              border-radius: 50%;
              background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
              box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4), 
                          0 8px 40px rgba(99, 102, 241, 0.2),
                          inset 0 2px 4px rgba(255,255,255,0.3);
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              border: 3px solid white;
              transform: translateZ(0);
              padding: 4px;
              box-sizing: border-box;
            ">
              <div style="
                font-size: ${numberFontSize};
                font-weight: 800;
                color: white;
                text-shadow: 0 2px 4px rgba(0,0,0,0.2);
                line-height: 1;
                white-space: nowrap;
              ">${this.count}</div>
              <div style="
                font-size: ${labelFontSize};
                font-weight: 600;
                color: rgba(255,255,255,0.9);
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-top: 2px;
                white-space: nowrap;
              ">events</div>
            </div>
          </div>
        `;

        // Add hover effect
        div.addEventListener('mouseenter', () => {
          div.querySelector('.cluster-wrapper').style.transform = 'scale(1.15)';
          div.querySelector('.cluster-circle').style.boxShadow = 
            '0 6px 30px rgba(99, 102, 241, 0.5), 0 12px 60px rgba(99, 102, 241, 0.3), inset 0 2px 4px rgba(255,255,255,0.3)';
        });
        div.addEventListener('mouseleave', () => {
          div.querySelector('.cluster-wrapper').style.transform = 'scale(1)';
          div.querySelector('.cluster-circle').style.boxShadow = 
            '0 4px 20px rgba(99, 102, 241, 0.4), 0 8px 40px rgba(99, 102, 241, 0.2), inset 0 2px 4px rgba(255,255,255,0.3)';
        });

        // Click to zoom in
        div.addEventListener('click', () => {
          this.onClick();
        });

        this.div = div;
        try {
          const panes = this.getPanes();
          if (panes && panes.overlayMouseTarget) {
            panes.overlayMouseTarget.appendChild(div);
          }
        } catch (error) {
          console.debug('Error adding cluster marker to panes:', error);
        }
      }

      draw() {
        try {
          // Safety checks for all Google Maps API components
          if (!this.div) return;
          if (!window.google || !window.google.maps || !window.google.maps.LatLng) return;
          
          const overlayProjection = this.getProjection();
          if (!overlayProjection || typeof overlayProjection.fromLatLngToDivPixel !== 'function') return;
          
          // Validate position data
          if (!this.position || typeof this.position.lat !== 'number' || typeof this.position.lng !== 'number') return;
          
          const latLng = new window.google.maps.LatLng(this.position.lat, this.position.lng);
          if (!latLng) return;
          
          const position = overlayProjection.fromLatLngToDivPixel(latLng);
          
          if (position && typeof position.x === 'number' && typeof position.y === 'number') {
            this.div.style.left = (position.x - 40) + 'px';
            this.div.style.top = (position.y - 40) + 'px';
          }
        } catch (error) {
          // Silently handle draw errors
          console.debug('Cluster draw error:', error);
        }
      }

      onRemove() {
        try {
          if (this.div && this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
          this.div = null;
        } catch (error) {
          // Silently handle removal errors
          console.debug('Cluster removal error:', error);
        }
      }
    }

    const marker = new ClusterMarker(
      cluster.position,
      cluster.events.length,
      () => {
        // Zoom in to cluster area when clicked
        map.setZoom(Math.min(map.getZoom() + 3, 18));
        map.panTo(cluster.position);
      }
    );

    marker.setMap(map);
    return marker;
  };

  // This function is called after markers are created
  const finalizeMarkers = (markers) => {
    markersRef.current = markers;
  };

  // Add or update user location marker using Google Maps API
  const updateUserLocationMarker = (location, map) => {
    if (!map || !window.google || !window.google.maps) return;
    if (!location || !location.lat || !location.lng) return;

    try {
      // Remove existing user location markers
      if (userLocationMarkerRef.current) {
        if (userLocationMarkerRef.current.outerCircle) {
          userLocationMarkerRef.current.outerCircle.setMap(null);
        }
        if (userLocationMarkerRef.current.innerCircle) {
          userLocationMarkerRef.current.innerCircle.setMap(null);
        }
        if (userLocationMarkerRef.current.dot) {
          userLocationMarkerRef.current.dot.setMap(null);
        }
      }

      // Create pulsing circle effect using Google Maps Circle API
      const outerCircle = new window.google.maps.Circle({
        center: location,
        radius: 30, // 30 meters radius for outer pulse
        fillColor: '#4285f4',
        fillOpacity: 0.15,
        strokeColor: '#4285f4',
        strokeOpacity: 0.3,
        strokeWeight: 1,
        map: map,
        clickable: false,
        zIndex: 1000
      });

      // Inner accuracy circle
      const innerCircle = new window.google.maps.Circle({
        center: location,
        radius: 15, // 15 meters radius
        fillColor: '#4285f4',
        fillOpacity: 0.3,
        strokeColor: '#4285f4',
        strokeOpacity: 0.5,
        strokeWeight: 2,
        map: map,
        clickable: false,
        zIndex: 1001
      });

      // Center dot marker
      const dot = new window.google.maps.Marker({
        position: location,
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#4285f4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
          scale: 8
        },
        clickable: false,
        zIndex: 1002
      });

      // Store references
      userLocationMarkerRef.current = {
        outerCircle,
        innerCircle,
        dot
      };

      // Add pulsing animation using setInterval
      let scale = 1;
      let growing = true;
      const pulseInterval = setInterval(() => {
        if (!userLocationMarkerRef.current || !userLocationMarkerRef.current.outerCircle) {
          clearInterval(pulseInterval);
          return;
        }

        if (growing) {
          scale += 0.02;
          if (scale >= 1.5) growing = false;
        } else {
          scale -= 0.02;
          if (scale <= 1) growing = true;
        }

        const newRadius = 30 * scale;
        const opacity = 0.15 * (1.5 - scale + 0.5);
        
        try {
          outerCircle.setOptions({
            radius: newRadius,
            fillOpacity: Math.max(0.05, opacity)
          });
        } catch (e) {
          clearInterval(pulseInterval);
        }
      }, 50);

      // Store interval reference for cleanup
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.pulseInterval = pulseInterval;
      }

      console.log('✅ User location marker added at:', location);
    } catch (error) {
      console.error('Error adding user location marker:', error);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current) return;

    // Ensure Google Maps API is fully loaded
    if (!window.google || !window.google.maps || !window.google.maps.Map) {
      console.warn('Google Maps API not ready, retrying...');
      setTimeout(initializeMap, 100);
      return;
    }

    try {
      // Use user's location if available, otherwise default to Indiranagar, Bengaluru
      const center = userLocation || { lat: 12.9716, lng: 77.6412 };

      // Adjust zoom based on device
      const initialZoom = window.innerWidth <= 768 ? 11 : 13;
      
      const map = new window.google.maps.Map(mapRef.current, {
        zoom: initialZoom,
        center: center,
        minZoom: 10,  // Prevent extreme zoom out to world map
        maxZoom: 19,  // Allow zooming in to street level
        restriction: {
          // Restrict map to reasonable bounds around India
          latLngBounds: {
            north: 35.5,
            south: 6.5,
            east: 97.5,
            west: 68.0
          },
          strictBounds: false  // Allow panning slightly outside bounds
        },
        styles: [
          // Snapchat-inspired map style - ultra clean and minimal
          // Water - soft, muted blue-grey with no labels
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#d4e7f5' }, { lightness: 10 }]
          },
          {
            featureType: 'water',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          // Landscape - very light cream/off-white background
          {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [{ color: '#fafaf8' }]
          },
          // Parks and green spaces - very soft, pastel green
          {
            featureType: 'landscape.natural',
            elementType: 'geometry',
            stylers: [{ color: '#e8f5e8' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#e8f5e8' }]
          },
          // Roads - extremely subtle, almost invisible
          {
            featureType: 'road',
            elementType: 'geometry.fill',
            stylers: [{ color: '#ffffff' }, { lightness: 20 }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#f0f0f0' }, { weight: 0.5 }]
          },
          // Highway - slightly more visible but still subtle
          {
            featureType: 'road.highway',
            elementType: 'geometry.fill',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#ebebeb' }, { weight: 0.8 }]
          },
          // Arterial roads - very light grey
          {
            featureType: 'road.arterial',
            elementType: 'geometry.fill',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'road.arterial',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#f5f5f5' }, { weight: 0.5 }]
          },
          // Local roads - barely visible
          {
            featureType: 'road.local',
            elementType: 'geometry',
            stylers: [{ color: '#fafafa' }, { weight: 0.3 }]
          },
          // ALL road labels - light grey when visible
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#c0c0c0' }, { weight: 0.5 }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#ffffff' }, { weight: 3 }]
          },
          // City/locality names - ALWAYS visible at all zoom levels
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.fill',
            stylers: [{ visibility: 'on' }, { color: '#9e9e9e' }, { weight: 1 }]
          },
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.stroke',
            stylers: [{ visibility: 'on' }, { color: '#ffffff' }, { weight: 3 }]
          },
          // Neighborhood labels - visible light grey
          {
            featureType: 'administrative.neighborhood',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#b8b8b8' }, { weight: 0.5 }]
          },
          {
            featureType: 'administrative.neighborhood',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#ffffff' }, { weight: 2.5 }]
          },
          // Hide all POI labels and icons completely
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [{ visibility: 'off' }]
          },
          // Hide all POI business markers
          {
            featureType: 'poi.business',
            stylers: [{ visibility: 'off' }]
          },
          // Hide transit completely
          {
            featureType: 'transit',
            stylers: [{ visibility: 'off' }]
          },
          // Buildings - extremely subtle
          {
            featureType: 'poi.business',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry',
            stylers: [{ color: '#f7f7f5' }]
          },
          // Province/country labels off
          {
            featureType: 'administrative.province',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'administrative.country',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          // Remove all border lines
          {
            featureType: 'administrative',
            elementType: 'geometry.stroke',
            stylers: [{ visibility: 'off' }]
          }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        disableDefaultUI: true,  // Disable all default UI controls
        gestureHandling: window.innerWidth <= 768 ? 'greedy' : 'greedy',  // greedy = one-finger pan, two-finger zoom
        zoomControl: false,
      });

      // Disable info windows on map clicks
      map.addListener('click', (e) => {
        // Prevent default info window
        e.stop();
      });

      mapInstanceRef.current = map;
      
      // Add zoom constraint listener to prevent extreme zoom out
      map.addListener('zoom_changed', () => {
        try {
          const currentZoom = map.getZoom();
          if (currentZoom < 10) {
            // Prevent zoom out below minimum
            map.setZoom(10);
            toast.info('Zoom limit reached');
          } else if (currentZoom > 19) {
            // Prevent zoom in beyond maximum
            map.setZoom(19);
          }
        } catch (error) {
          console.debug('Zoom constraint error:', error);
        }
      });
      
      // Add zoom listener for progressive label reveal (Ultra minimal - 80% fewer labels)
      map.addListener('zoom_changed', () => {
        try {
          if (!map || !map.getZoom) return;
          const zoom = map.getZoom();
          if (!zoom || zoom === undefined) return;
        
        // Base styles - Ultra minimal design with 80% fewer labels
        const baseStyles = [
          // Core styling - matches initial setup
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d4e7f5' }, { lightness: 10 }] },
          { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#fafaf8' }] },
          { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#e8f5e8' }] },
          { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e8f5e8' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }, { lightness: 20 }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#f0f0f0' }, { weight: 0.5 }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
          { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#ebebeb' }, { weight: 0.8 }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'administrative.province', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'administrative.country', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
          { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#f7f7f5' }] }
        ];
        
        // Place name visibility based on zoom level
        // Zoom < 14 = ~4km+ view - Show ONLY main area names (cities/localities)
        if (zoom < 14) {
          // Very zoomed out (>4km radius) - show ONLY main city/locality names, hide everything else
          baseStyles.push({ featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ featureType: 'poi', stylers: [{ visibility: 'off' }] });
          
          // ALWAYS show main locality/city names at all zoom levels - readable
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#a0a0a0' }, { weight: 0.9 }] 
          });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3 }] 
          });
        } else if (zoom < 16) {
          // Medium zoom (2-3km radius) - SHOW place names, neighborhoods, AND landmarks/POIs
          baseStyles.push({ featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          
          // Show neighborhood labels - readable
          baseStyles.push({ 
            featureType: 'administrative.neighborhood', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#9e9e9e' }, { weight: 0.8 }] 
          });
          baseStyles.push({ 
            featureType: 'administrative.neighborhood', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 2.5 }] 
          });
          
          // Show locality labels - readable
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#8e8e8e' }, { weight: 1 }] 
          });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3 }] 
          });
          
          // Hide all POI elements completely
          baseStyles.push({ featureType: 'poi', stylers: [{ visibility: 'off' }] });
        } else if (zoom < 17) {
          // Getting closer - show highways and POI landmarks
          baseStyles.push({ 
            featureType: 'road.highway', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#b0b0b0' }, { weight: 0.9 }] 
          });
          baseStyles.push({ 
            featureType: 'road.highway', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3 }] 
          });
          baseStyles.push({ featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#a8a8a8' }, { weight: 1 }] 
          });
          
          // Hide all POI elements completely
          baseStyles.push({ featureType: 'poi', stylers: [{ visibility: 'off' }] });
        } else if (zoom < 18) {
          // Close zoom - highways, arterial roads, and POIs visible
          baseStyles.push({ 
            featureType: 'road.highway', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#9e9e9e' }, { weight: 1 }] 
          });
          baseStyles.push({ 
            featureType: 'road.highway', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3.5 }] 
          });
          baseStyles.push({ 
            featureType: 'road.arterial', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'simplified' }, { color: '#b8b8b8' }, { weight: 0.8 }] 
          });
          baseStyles.push({ 
            featureType: 'road.arterial', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 2.5 }] 
          });
          baseStyles.push({ featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#9e9e9e' }, { weight: 1 }] 
          });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3 }] 
          });
          
          // Hide all POI elements completely
          baseStyles.push({ featureType: 'poi', stylers: [{ visibility: 'off' }] });
        } else {
          // Very close zoom (18+) - show all roads
          baseStyles.push({ 
            featureType: 'road.highway', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#8e8e8e' }, { weight: 1.2 }] 
          });
          baseStyles.push({ 
            featureType: 'road.highway', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 4 }] 
          });
          baseStyles.push({ 
            featureType: 'road.arterial', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#a0a0a0' }, { weight: 1 }] 
          });
          baseStyles.push({ 
            featureType: 'road.arterial', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3 }] 
          });
          baseStyles.push({ 
            featureType: 'road.local', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#c0c0c0' }, { weight: 0.8 }] 
          });
          baseStyles.push({ 
            featureType: 'road.local', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 2.5 }] 
          });
          baseStyles.push({ featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.fill', 
            stylers: [{ visibility: 'on' }, { color: '#9e9e9e' }, { weight: 1 }] 
          });
          baseStyles.push({ 
            featureType: 'administrative.locality', 
            elementType: 'labels.text.stroke', 
            stylers: [{ color: '#ffffff' }, { weight: 3 }] 
          });
          
          // Hide all POI elements completely
          baseStyles.push({ featureType: 'poi', stylers: [{ visibility: 'off' }] });
        }
        
        if (map && map.setOptions) {
          map.setOptions({ styles: baseStyles });
        }
        
        // Track zoom level and re-render markers for clustering
        setCurrentZoom(zoom);
        } catch (error) {
          console.debug('Zoom change error:', error);
        }
      });

      // Add markers to map
      const initialMarkers = addMarkersToMap(map);
      finalizeMarkers(initialMarkers);
      setIsLoading(false);
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError(true);
      setIsLoading(false);
      toast.error('Error loading map interface');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      music: '#f97316',
      gaming: '#3b82f6',
      food: '#22c55e',
      sports: '#eab308',
      art: '#a855f7',
      arts: '#a855f7',
      social: '#ec4899',
      screenings: '#06b6d4',
      parties: '#f43f5e',
      default: '#6366f1'
    };
    return colors[category] || colors.default;
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      music: 'linear-gradient(90deg, #f97316, #fb923c)',
      gaming: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
      food: 'linear-gradient(90deg, #22c55e, #4ade80)',
      sports: 'linear-gradient(90deg, #eab308, #fbbf24)',
      art: 'linear-gradient(90deg, #a855f7, #c084fc)',
      arts: 'linear-gradient(90deg, #a855f7, #c084fc)',
      social: 'linear-gradient(90deg, #ec4899, #f472b6)',
      screenings: 'linear-gradient(90deg, #06b6d4, #22d3ee)',
      parties: 'linear-gradient(90deg, #f43f5e, #fb7185)',
      default: 'linear-gradient(90deg, #6366f1, #818cf8)'
    };
    return gradients[category] || gradients.default;
  };

  const handleRecenter = () => {
    if (mapInstanceRef.current) {
      // Get current actual location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            // Pan map to new location
            mapInstanceRef.current.panTo(newLocation);
            mapInstanceRef.current.setZoom(14);
            
            // Notify parent component to update location and re-check Bangalore status
            if (onRecenter) {
              onRecenter(newLocation, true); // true = actual GPS location
            }
            
            toast.success('Map recentered to your current location');
          },
          (error) => {
            // Fallback to existing userLocation if available
            if (userLocation) {
              mapInstanceRef.current.panTo(userLocation);
              mapInstanceRef.current.setZoom(14);
              toast.info('Using last known location');
            } else {
              // Default to Bangalore
              const defaultLocation = { lat: 12.9716, lng: 77.6412 };
              mapInstanceRef.current.panTo(defaultLocation);
              mapInstanceRef.current.setZoom(13);
              
              if (onRecenter) {
                onRecenter(defaultLocation, false); // false = not actual GPS
              }
              toast.info('Recentered to Bangalore');
            }
          }
        );
      } else {
        // No geolocation support - fallback to userLocation or default
        if (userLocation) {
          mapInstanceRef.current.panTo(userLocation);
          mapInstanceRef.current.setZoom(14);
          toast.success('Map recentered to your location');
        } else {
          const defaultLocation = { lat: 12.9716, lng: 77.6412 };
          mapInstanceRef.current.panTo(defaultLocation);
          mapInstanceRef.current.setZoom(13);
          
          if (onRecenter) {
            onRecenter(defaultLocation, false); // false = not actual GPS
          }
          toast.info('Recentered to Bangalore');
        }
      }
    }
  };

  // Fallback mock interface - Beautiful visual map representation
  if (mapError) {
    return (
      <div className="w-full h-full gradient-hero relative overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        {/* Map Preview Notice */}
        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-card/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-border flex items-center space-x-2">
            <div className="text-xl">🗺️</div>
            <div className="text-sm">
              <span className="font-semibold text-foreground">Preview Mode</span>
              <span className="text-muted-foreground ml-2">• Click any event below</span>
            </div>
          </div>
        </div>

        {/* Events Grid */}
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="w-full max-w-6xl">
            {filteredEvents.length === 0 ? (
              <div className="text-center p-8 bg-card/95 backdrop-blur-sm rounded-xl shadow-lg">
                <div className="text-4xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-foreground mb-2">No events found</h3>
                <p className="text-sm text-muted-foreground">Try searching with different keywords</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredEvents.map((event, index) => (
                <button
                  key={event.id}
                  onClick={() => onEventSelect(event)}
                  className="group relative p-5 bg-card/95 backdrop-blur-sm hover:bg-card rounded-xl transition-smooth shadow-md hover:shadow-xl border border-border hover:border-primary/50 text-left animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Category indicator */}
                  <div 
                    className="absolute top-0 left-0 w-full h-1 rounded-t-xl"
                    style={{ background: getCategoryGradient(event.category) }}
                  />
                  
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="text-3xl flex-shrink-0 group-hover:scale-110 transition-smooth">
                      {event.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-smooth">
                        {event.title}
                      </h3>
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <span className="capitalize">{event.category}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-xs text-muted-foreground">
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="truncate">{event.location}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-muted-foreground">
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{event.time.split(' - ')[0]}</span>
                      </div>
                      <div className="text-xs font-medium capitalize">
                        <span className={event.type === 'open' ? 'text-accent' : 'text-secondary'}>
                          {event.type}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-smooth rounded-xl pointer-events-none" />
                </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info footer */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-card/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-border">
            <p className="text-xs text-muted-foreground">
              💡 <span className="font-medium text-foreground">Tip:</span> Add your Google Maps API key for the full interactive map experience
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50">
          <div className="flex flex-col items-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Recenter Button - Gen Z style with glow effect - Fixed position on mobile/iPad */}
      {!isLoading && !mapError && (
        <Button
          onClick={handleRecenter}
          className="fixed lg:absolute bottom-24 right-4 z-50 h-12 w-12 lg:h-14 lg:w-14 rounded-full shadow-lg text-white hover:scale-110 transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.5), 0 0 20px rgba(118, 75, 162, 0.3)'
          }}
          size="icon"
          title="Recenter map to my location"
        >
          <Navigation className="h-5 w-5 lg:h-6 lg:w-6" />
        </Button>
      )}
      
      {/* Add CSS for Gen Z style animations */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: scale(1) translateY(0); }
          50% { transform: scale(1.2) translateY(-10px); }
        }
        
        @keyframes marker-pulse {
          0%, 100% { 
            transform: scale(1);
            filter: brightness(1);
          }
          50% { 
            transform: scale(1.05);
            filter: brightness(1.1);
          }
        }
        
        .custom-marker:hover .marker-wrapper {
          transform: scale(1.15) translateY(-4px) !important;
          filter: drop-shadow(0 0 16px currentColor) drop-shadow(0 8px 32px rgba(0,0,0,0.3)) !important;
        }
        
        .custom-marker:hover .marker-circle {
          animation: none !important;
          box-shadow: 0 12px 32px currentColor, 0 0 30px currentColor, inset 0 1px 2px rgba(255,255,255,0.8) !important;
        }
      `}</style>
    </div>
  );
}
